import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Link,
  Tooltip,
  CircularProgress,
} from "@mui/material"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import i18n from "i18n"
import countryList from "react-select-country-list"
import AuthContext from "context/auth/authContext"
import InvoicingSpainModal from "./Integrations/InvoicingSpain/InvoicingSpainModal"
import ConfirmationModal from "common/ConfirmationModal"
import usePartyStatus from "./hooks/usePartyStatus"
import { API } from "aws-amplify"

// Una organización ya registrada en Invopop todavía no puede desvincularse, así que se le
// oculta el botón. En pending/processing el alta ni se completó, así que ahí sigue disponible.
const SHOW_DISCONNECT_BUTTON = false

const ModalIntegrationsSettings = ({ open, onClose, organizationFields }) => {
  const { user } = useContext(AuthContext)
  const [openInvoicingSpainModal, setOpenInvoicingSpainModal] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [disconnectError, setDisconnectError] = useState(null)

  const {
    status: invoicingSpainStatus,
    linkUrl: invoicingSpainLinkUrl,
    loading: loadingStatus,
    refetch: fetchPartyStatus,
  } = usePartyStatus(
    organizationFields?.organizationId,
    open && !!organizationFields?.organizationId,
  )

  // PARCHE TEMPORAL — corregir de raíz.
  // `custom:pais` persiste el LABEL del país, no el código ISO, así que su valor
  // depende de por dónde se escribió: "Spain" (label de react-select-country-list,
  // que está en inglés) si se eligió en el desplegable del perfil, o "España" si lo
  // rellenaron los fallbacks hardcodeados de ProfileComponent.js. Ese segundo caso
  // rompía el gating: getValue("España") es undefined, isSpain quedaba false y el
  // botón Conectar se bloqueaba con "requiresSpain" aunque el país fuese España.
  // Aquí se aceptan ambos idiomas y el ISO: native() SUMA los labels nativos al mapa
  // inglés (no lo reemplaza) y compara sin distinguir mayúsculas.
  // Arreglo de fondo pendiente: persistir el código ISO en `custom:pais` en vez del
  // label, migrar los valores ya guardados en Cognito, unificar las dos fuentes de
  // países en conflicto (constants/countries.js en castellano vs
  // react-select-country-list en inglés) y quitar los fallbacks que inventan
  // `label: "España"` en ProfileComponent.js. Al hacerlo, eliminar este parche.
  const pais = (user?.["custom:pais"] || "").trim()
  const isSpain =
    pais.toUpperCase() === "ES" ||
    countryList().native().getValue(pais) === "ES"
  const hasCif = !!organizationFields?.cif_number
  const canConnect = isSpain && hasCif
  const hasExistingParty =
    !!invoicingSpainLinkUrl ||
    ["pending", "processing"].includes(invoicingSpainStatus)
  const isRegistered = invoicingSpainStatus === "registered"
  const isPending = ["pending", "processing"].includes(invoicingSpainStatus)
  const isBusy = loadingStatus || disconnecting

  const getStatusLabel = status => {
    if (loadingStatus && status == null)
      return i18n.t("integrationsSettings.connecting")
    switch (status) {
      case "registered":
        return i18n.t("integrationsSettings.connected")
      case "pending":
      case "processing":
        return i18n.t("integrationsSettings.pending")
      case "cancelled":
      default:
        return i18n.t("integrationsSettings.disconnected")
    }
  }

  const handleInvoicingSpainConnect = () => {
    if (!hasExistingParty && !canConnect) return
    setOpenInvoicingSpainModal(true)
  }

  const handleInvoicingSpainDisconnect = () => {
    if (disconnecting) return
    setDisconnectError(null)
    setShowDisconnectConfirm(true)
  }

  const handleDisconnectConfirmClose = () => {
    if (disconnecting) return
    setShowDisconnectConfirm(false)
  }

  const handleDisconnectConfirm = async () => {
    if (disconnecting) return
    setDisconnectError(null)
    setDisconnecting(true)
    try {
      await API.post("CORE", "/invopop/verifactu/unregister_party", {
        body: {
          data: { organizationId: organizationFields.organizationId },
        },
      })
      setShowDisconnectConfirm(false)
      await fetchPartyStatus()
    } catch (error) {
      console.error(
        "party-unregister-failed:",
        error?.response?.data?.code || error?.message || "unknown",
      )
      setDisconnectError(i18n.t("integrationsSettings.disconnectError"))
    } finally {
      setDisconnecting(false)
    }
  }

  const connectDisabledReason = !isSpain
    ? i18n.t("integrationsSettings.requiresSpain")
    : !hasCif
      ? i18n.t("integrationsSettings.requiresCif")
      : ""

  const connectButtonTooltip = isRegistered
    ? i18n.t("integrationsSettings.disconnectViaSupport")
    : connectDisabledReason && !hasExistingParty
      ? connectDisabledReason
      : ""

  // Ya conectado: desvincularse de Verifactu tiene implicaciones fiscales, así que no se
  // ofrece por interfaz. Se muestra "Desconectar" deshabilitado en vez de "Conectar" para
  // que el botón refleje el estado real, y el tooltip deriva a soporte.
  const connectButton = (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        variant={isRegistered ? "outlined" : "contained"}
        color="branded"
        sx={{ height: 36 }}
        onClick={handleInvoicingSpainConnect}
        disabled={isBusy || isRegistered || (!canConnect && !hasExistingParty)}
      >
        {isBusy ? (
          <CircularProgress size={20} color="inherit" />
        ) : isRegistered ? (
          i18n.t("integrationsSettings.disconnect")
        ) : isPending ? (
          i18n.t("integrationsSettings.continueSetup")
        ) : (
          i18n.t("integrationsSettings.connect")
        )}
      </Button>
      {((SHOW_DISCONNECT_BUTTON && isRegistered) || isPending) && (
        <Button
          variant="outlined"
          color="error"
          sx={{ height: 36 }}
          onClick={handleInvoicingSpainDisconnect}
          disabled={isBusy}
        >
          {i18n.t("integrationsSettings.disconnect")}
        </Button>
      )}
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("integrationsSettings.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ marginBottom: 2 }}>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              padding: 2,
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <span style={{ fontSize: "16px", color: "#414141" }}>
                {i18n.t("integrationsSettings.invoicingSpain")}
              </span>
              <Link
                href="https://help.planhopper.com/es/articles/12856221-como-activar-facturacion-electronica-para-espana-verifactu-en-tu-cuenta-de-planhopper"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: "14px", marginLeft: 1 }}
              >
                {i18n.t("integrationsSettings.viewHelpArticle")}
              </Link>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {connectButtonTooltip ? (
                <Tooltip title={connectButtonTooltip}>
                  <span>{connectButton}</span>
                </Tooltip>
              ) : (
                connectButton
              )}
              <Box sx={{ marginLeft: 2, color: "#6d6d6d", fontSize: "14px" }}>
                {i18n.t("integrationsSettings.status")}{" "}
                {getStatusLabel(invoicingSpainStatus)}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <span style={{ fontSize: "16px", color: "#414141" }}>
                {i18n.t("integrationsSettings.holded")}
              </span>
              <Link
                href="https://help.planhopper.com/es/articles/13632913-como-integrar-holded-a-tu-cuenta-de-planhopper"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: "14px", marginLeft: 1 }}
              >
                {i18n.t("integrationsSettings.viewHelpArticle")}
              </Link>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="contained"
                color="branded"
                sx={{ minWidth: 120 }}
                disabled
              >
                {i18n.t("integrationsSettings.connect")}
              </Button>
              <Box sx={{ marginLeft: 2, color: "#6d6d6d", fontSize: "14px" }}>
                {i18n.t("integrationsSettings.commingSoon")}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="branded" variant="contained">
          {i18n.t("integrationsSettings.close")}
        </Button>
      </DialogActions>
      <InvoicingSpainModal
        open={openInvoicingSpainModal}
        onClose={() => setOpenInvoicingSpainModal(false)}
        linkUrl={invoicingSpainLinkUrl}
        organizationFields={organizationFields}
        hasExistingParty={hasExistingParty}
        onPartyRegistered={fetchPartyStatus}
      />
      <ConfirmationModal
        open={showDisconnectConfirm}
        onClose={handleDisconnectConfirmClose}
        onConfirm={handleDisconnectConfirm}
        title={i18n.t("integrationsSettings.disconnectInvoicingSpainTitle")}
        message={
          disconnectError ||
          i18n.t("integrationsSettings.confirmDisconnectInvoicingSpain")
        }
        confirmButtonText={i18n.t("integrationsSettings.disconnect")}
        cancelButtonText={i18n.t("invoicingSpainWizard.cancel")}
        confirmButtonColor="error"
        confirmLoading={disconnecting}
      />
    </Dialog>
  )
}

ModalIntegrationsSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationFields: PropTypes.object.isRequired,
}

export default ModalIntegrationsSettings
